import React, { useCallback } from 'react';
import { pickAriaAndDataProps } from '@tanium/coreui-utils';
import { Box } from '@tanium/react-box';
import { ButtonBase, ButtonBaseProps } from '@tanium/react-button-base';
import styled from '@tanium/react-emotion';
import { ThemeDefinition } from '@tanium/react-theme-definition';
import { css } from '@tanium/style-system';
import { ThemeFontWeights } from '@tanium/theme-data';
import {
  ButtonBorder,
  ButtonFill,
  ButtonHoverFill,
  ButtonHoverText,
  ButtonSelectedBorder,
  ButtonSelectedFill,
  ButtonSelectedText,
  ButtonText,
} from './themeDefinitions';

interface ToggleEvent {
  /**
   * The new selected state.
   */
  selected: boolean;
}

export type ToggleEventHandler = (e: ToggleEvent) => void;

interface ToggleButtonInnerProps {
  /**
   * A callback to be invoked when the button is toggled.
   */
  onToggle?: ToggleEventHandler;
  /**
   * Whether the button should be selected or not.
   */
  selected?: boolean;
  /**
   * Size of the button.
   */
  size?: ButtonBaseProps['size'];
  /**
   * The button's content.
   */
  children?: React.ReactNode;
  /**
   * Color of icons in the button when not hovered.
   */
  iconColor?: ThemeDefinition;
}

export const StyledColorIcon = styled(
  Box,
  {},
)<{ size: ButtonBaseProps['size'] | undefined }>(({ size = 'lg' }) =>
  css({
    size: size === 'sm' ? '12px' : '16px',
    display: 'block',
  }),
);

export const StyledButtonBase = styled(ButtonBase, {
  shouldForwardProp: (prop) => !['selected', 'iconColor'].includes(prop),
})<{ selected: boolean | undefined; iconColor: ThemeDefinition | undefined }>(
  ({ selected }) =>
    css({
      bg: selected ? ButtonSelectedFill : ButtonFill,
      color: selected ? ButtonSelectedText : ButtonText,
      ['&:hover, &:active' as string]: {
        bg: ButtonHoverFill,
        color: ButtonHoverText,
      },
      border: 1,
      borderColor: selected ? ButtonSelectedBorder : ButtonBorder,
      fontWeight: ThemeFontWeights.Medium,
    }),
  ({ iconColor }) =>
    iconColor &&
    css({
      [`${StyledColorIcon}` as string]: {
        color: iconColor,
      },
      [`&:hover ${StyledColorIcon}, &:active ${StyledColorIcon}` as string]: {
        bg: ButtonHoverFill,
        color: ButtonHoverText,
      },
    }),
);

export const ToggleButtonInner = React.forwardRef<HTMLButtonElement, ToggleButtonInnerProps>(
  ({ size, selected, onToggle, children, iconColor, ...props }, ref) => {
    const handleClick = useCallback(() => {
      if (onToggle) {
        onToggle({ selected: !selected });
      }
    }, [onToggle, selected]);

    return (
      <StyledButtonBase
        aria-pressed={selected}
        {...pickAriaAndDataProps(props)}
        ref={ref}
        type="button"
        onClick={handleClick}
        size={size}
        border={1}
        selected={selected}
        iconColor={iconColor}
      >
        {children}
      </StyledButtonBase>
    );
  },
);

export type ToggleButtonProps = Omit<ToggleButtonInnerProps, 'iconColor'>;

/**
 * A toggle button.
 */
export const ToggleButton = React.forwardRef<HTMLButtonElement, ToggleButtonProps>((props, ref) => (
  <ToggleButtonInner {...props} ref={ref} />
));

ToggleButton.displayName = 'ToggleButton';
